import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/common';
import {
  DiscountVoucherFormErrors,
  DiscountVoucherResponse,
  CreateDiscountVoucherRequest,
  UpdateDiscountVoucherRequest,
  DiscountType,
} from '@/types/discountVoucher';
import {
  CustomerSearchInput,
} from '@/components/receiptVouchers';
import DiscountVoucherInvoiceSelector from './DiscountVoucherInvoiceSelector';
import { Customer } from '@/types/customer';

interface Props {
  mode: 'create' | 'edit';
  initialData?: DiscountVoucherResponse;
  initialCustomer?: Customer;
  onSubmit: (payload: CreateDiscountVoucherRequest | UpdateDiscountVoucherRequest) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
}

const DiscountVoucherForm: React.FC<Props> = ({
  mode,
  initialData,
  initialCustomer,
  onSubmit,
  onCancel,
  loading = false,
}) => {
  const { t } = useTranslation();

  const [formData, setFormData] = useState({
    customer_id: initialData?.customer_id || '',
    discount_type: (initialData?.discount_type as DiscountType) || 'percent',
    discount_value: initialData?.discount_value || 0,
    reason: initialData?.reason || '',
  });

  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [selectedInvoiceCodes, setSelectedInvoiceCodes] = useState<string[]>(
    initialData?.invoice_discounts?.map(i => i.invoice_code) || []
  );
  const [errors, setErrors] = useState<DiscountVoucherFormErrors>({});

  // Initialize customer from initialData or initialCustomer
  useEffect(() => {
    if (mode === 'edit' && initialData?.customer_id) {
      // We only know customer_code, không có full CustomerResponse ở đây,
      // nên chỉ set customer_id trong form, phần thông tin tài chính sẽ load từ BE ở component khác nếu cần.
      setFormData(prev => ({ ...prev, customer_id: initialData.customer_id }));
    } else if (mode === 'create' && initialCustomer) {
      setSelectedCustomer(initialCustomer);
      setFormData(prev => ({ ...prev, customer_id: initialCustomer.code }));
    }
  }, [mode, initialData, initialCustomer]);

  const handleChange = (field: keyof typeof formData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field as keyof DiscountVoucherFormErrors]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleInvoiceSelectionChange = (codes: string[]) => {
    setSelectedInvoiceCodes(codes);
    if (errors.invoice_items) {
      setErrors(prev => ({ ...prev, invoice_items: undefined }));
    }
  };

  const validate = (): boolean => {
    const newErrors: DiscountVoucherFormErrors = {};

    if (!formData.customer_id) {
      newErrors.customer_id = t('discountVoucher.validation.customerRequired', 'Vui lòng chọn khách hàng');
    }
    if (!formData.discount_type) {
      newErrors.discount_type = t('discountVoucher.validation.typeRequired', 'Vui lòng chọn loại chiết khấu');
    }
    if (!formData.discount_value || formData.discount_value <= 0) {
      newErrors.discount_value = t('discountVoucher.validation.valuePositive', 'Giá trị chiết khấu phải > 0');
    }
    if (selectedInvoiceCodes.length === 0) {
      newErrors.invoice_items = t('discountVoucher.validation.invoicesRequired', 'Vui lòng chọn ít nhất 1 hóa đơn');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    if (mode === 'create') {
      const payload: CreateDiscountVoucherRequest = {
        customer_id: formData.customer_id,
        discount_type: formData.discount_type as DiscountType,
        discount_value: formData.discount_value,
        reason: formData.reason,
        invoice_items: selectedInvoiceCodes.map(code => ({ invoice_code: code })),
      };
      await onSubmit(payload);
    } else {
      const payload: UpdateDiscountVoucherRequest = {
        discount_type: formData.discount_type as DiscountType,
        discount_value: formData.discount_value,
        reason: formData.reason,
        invoice_items: selectedInvoiceCodes.map(code => ({ invoice_code: code })),
      };
      await onSubmit(payload);
    }
  };

  const handleCustomerChange = (customer: Customer | null) => {
    setSelectedCustomer(customer);
    handleChange('customer_id', customer?.code || '');
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Customer section */}
      <div className="bg-gray-50 rounded-lg p-4 space-y-4">
        <h3 className="text-sm font-medium text-gray-900">
          {t('discountVoucher.sections.customer', 'Khách hàng')}
        </h3>
        <CustomerSearchInput
          selectedCustomer={selectedCustomer}
          onCustomerChange={handleCustomerChange}
          error={errors.customer_id}
        />
      </div>

      {/* Discount info */}
      <div className="bg-gray-50 rounded-lg p-4 space-y-4">
        <h3 className="text-sm font-medium text-gray-900">
          {t('discountVoucher.sections.discount', 'Thông tin chiết khấu')}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('discountVoucher.fields.discountType', 'Loại chiết khấu')}
            </label>
            <select
              value={formData.discount_type}
              onChange={e => handleChange('discount_type', e.target.value as DiscountType)}
              className="mt-1 block w-full px-3 py-2 rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            >
              <option value="percent">{t('discountVoucher.types.percent', 'Theo % trên nợ còn lại')}</option>
              <option value="amount">{t('discountVoucher.types.amount', 'Số tiền cố định trên nợ còn lại')}</option>
            </select>
            {errors.discount_type && (
              <p className="mt-1 text-sm text-red-600">{errors.discount_type}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('discountVoucher.fields.discountValue', 'Giá trị chiết khấu')}
            </label>
            <input
              type="number"
              value={formData.discount_value}
              onChange={e => {
                const value = e.target.value;
                if (value === '' || value === null || value === undefined) {
                  handleChange('discount_value', 0);
                } else {
                  const numValue = parseFloat(value);
                  if (!isNaN(numValue) && numValue >= 0) {
                    handleChange('discount_value', numValue);
                  }
                }
              }}
              onWheel={(e) => e.currentTarget.blur()}
              min="0"
              step="any"
              className="mt-1 block w-full px-3 py-2 rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            />
            {errors.discount_value && (
              <p className="mt-1 text-sm text-red-600">{errors.discount_value}</p>
            )}
          </div>
        </div>
      </div>

      {/* Invoice selection */}
      <DiscountVoucherInvoiceSelector
        customerId={formData.customer_id}
        selectedInvoiceCodes={selectedInvoiceCodes}
        onChange={handleInvoiceSelectionChange}
        discountType={formData.discount_type}
        discountValue={formData.discount_value}
      />
      {errors.invoice_items && (
        <p className="mt-1 text-sm text-red-600">{errors.invoice_items}</p>
      )}

      {/* Reason */}
      <div className="bg-gray-50 rounded-lg p-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t('discountVoucher.fields.reason', 'Lý do chiết khấu')}
        </label>
        <textarea
          value={formData.reason}
          onChange={e => handleChange('reason', e.target.value)}
          rows={3}
          className="mt-1 block w-full px-3 py-2 rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
        />
        {errors.reason && (
          <p className="mt-1 text-sm text-red-600">{errors.reason}</p>
        )}
      </div>

      {/* Actions */}
      <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
        <Button type="button" variant="secondary" onClick={onCancel} disabled={loading}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" loading={loading}>
          {mode === 'create'
            ? t('discountVoucher.actions.create', 'Tạo phiếu chiết khấu')
            : t('discountVoucher.actions.update', 'Cập nhật phiếu chiết khấu')}
        </Button>
      </div>
    </form>
  );
};

export default DiscountVoucherForm;
